import { useEffect, useRef, useState } from 'react';
import { Workspace } from '../../apis/api-client';
import { workspacesClient } from '../../apis/client-instances';
import { Autocomplete, CircularProgress, TextField } from '../ui';

const SEARCH_PAGE_SIZE = 25;
const DEBOUNCE_MS = 250;
// Sensible default width so a bare <WorkspaceAutocomplete /> fits nicely in a
// filter row without every caller pinning its own width. Override via `sx`.
const DEFAULT_MIN_WIDTH = 220;

export interface WorkspaceAutocompleteProps {
  value: Workspace | null;
  onChange: (workspace: Workspace | null) => void;
  label?: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  sx?: any;
}

/**
 * Lazy-loaded workspace picker.
 *
 * - Fetches the first page of results when opened.
 * - Debounces server-side search as the user types.
 * - The server is authoritative — client-side option filtering is disabled.
 *
 * Use this anywhere a workspace dropdown would otherwise load every workspace
 * at once. Hydrating a current selection (e.g. from localStorage) is the
 * caller's job — pass the resolved Workspace as `value`.
 */
export default function WorkspaceAutocomplete({
  value,
  onChange,
  label,
  placeholder = 'All Workspaces',
  size = 'small',
  fullWidth = false,
  disabled = false,
  required = false,
  error = false,
  helperText,
  sx,
}: WorkspaceAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Workspace[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);

  const reqIdRef = useRef(0);

  useEffect(() => {
    if (!open) return;

    const id = ++reqIdRef.current;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const result = await workspacesClient.searchWorkspaces(
          inputValue || undefined,
          1,
          SEARCH_PAGE_SIZE
        );
        if (id !== reqIdRef.current) return;
        setOptions(result.items ?? []);
      } catch {
        if (id !== reqIdRef.current) return;
        setOptions([]);
      } finally {
        if (id === reqIdRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [inputValue, open]);

  return (
    <Autocomplete<Workspace, false, false, false>
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value}
      onChange={(_e, newValue) => onChange(newValue)}
      inputValue={inputValue}
      onInputChange={(_e, val) => setInputValue(val)}
      options={options}
      loading={loading}
      filterOptions={(x) => x}
      getOptionLabel={(option) => option?.name ?? ''}
      isOptionEqualToValue={(opt, val) => opt.id === val?.id}
      noOptionsText={inputValue ? 'No workspaces match' : 'Type to search'}
      sx={{
        minWidth: DEFAULT_MIN_WIDTH,
        // Hide the clear (X) button when there's nothing selected — it's only
        // meaningful for clearing a selection, not for wiping in-progress typing.
        '& .MuiAutocomplete-clearIndicator': {
          visibility: value ? 'visible' : 'hidden',
        },
        ...sx,
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
